import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ConfirmAccount from "./pages/ConfirmAccount";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import Absences from "./pages/Absences";
import Expenses from "./pages/Expenses";
import Interviews from "./pages/Interviews";
import RSE from "./pages/RSE";

import Admin from "./pages/Admin";
import Habilitations from "./pages/Habilitations";
import Payroll from "./pages/Payroll";
import SuperAdmin from "./pages/SuperAdmin";
import Subscription from "./pages/Subscription";
import Documents from "./pages/Documents";
import SEPAMandate from "./pages/SEPAMandate";
import EmployeePortal from "./pages/EmployeePortal";
import Settings from "./pages/Settings";
import Formations from "./pages/Formations";
import Reporting from "./pages/Reporting";
import AuditLog from "./pages/AuditLog";
import Planning from "./pages/Planning";
import Emargement from "./pages/Emargement";
import Prospects from "./pages/Prospects";
import SousTraitants from "./pages/SousTraitants";
import Layout from "./components/Layout";

// Module names for display
const MODULE_NAMES = {
  'employees': 'Gestion des employés',
  'habilitations': 'Habilitations',
  'conges': 'Gestion des congés',
  'payroll': 'Préparation de la paie',
  'documents': 'Documents',
  'entretiens': 'Entretiens',
  'expenses': 'Notes de frais',
  'rse': 'RSE',
  'ai_chat': 'Assistant IA',
  'calendar': 'Calendrier',
  'admin': 'Administration',
  'dashboard': 'Tableau de bord',
  'formations': 'Formation continue',
  'reporting': 'Reporting RH',
  'planning': 'Gestion des plannings',
  'emargement': 'Émargement et présences',
  'prospects': 'Prospects commerciaux',
  'sous_traitants': 'Sous-traitants'
};

// Protected Route Component with subscription and module check
const ProtectedRoute = ({ children, requireSubscription = true, requiredModule = null }) => {
  const { user, loading, needsSubscription, hasModuleAccess } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check if subscription is required and user needs to subscribe
  if (requireSubscription && needsSubscription()) {
    return <Navigate to="/subscription" replace />;
  }

  // Check module access based on subscription package
  // hasModuleAccess already handles: super_admin bypass, trial all_access, and package restrictions
  if (requiredModule && user.role !== 'super_admin' && !hasModuleAccess(requiredModule)) {
    const moduleName = MODULE_NAMES[requiredModule] || requiredModule;
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-6 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Module non disponible
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-2">
            <span className="font-semibold text-[#0F5156]">{moduleName}</span>
          </p>
          <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">
            Votre abonnement actuel ne donne pas accès à ce module.
            Passez à un forfait supérieur pour débloquer cette fonctionnalité.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => window.location.href = '/subscription?action=upgrade'}
              className="px-8 py-3 bg-[#0F5156] text-white font-medium rounded-lg hover:bg-[#0d464a] transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Mettre à niveau mon abonnement
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Retour
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children, redirectIfAuth = true }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  if (user && redirectIfAuth) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicRoute redirectIfAuth={false}>
                <Landing />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/confirm-account"
            element={
              <PublicRoute redirectIfAuth={false}>
                <ConfirmAccount />
              </PublicRoute>
            }
          />
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredModule="dashboard">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute requiredModule="employees">
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/absences"
            element={
              <ProtectedRoute requiredModule="conges">
                <Absences />
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute requiredModule="expenses">
                <Expenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/interviews"
            element={
              <ProtectedRoute requiredModule="entretiens">
                <Interviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rse"
            element={
              <ProtectedRoute requiredModule="rse">
                <RSE />
              </ProtectedRoute>
            }
          />
          <Route
            path="/habilitations"
            element={
              <ProtectedRoute requiredModule="habilitations">
                <Habilitations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll"
            element={
              <ProtectedRoute requiredModule="payroll">
                <Payroll />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning"
            element={
              <ProtectedRoute requiredModule="planning">
                <Planning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emargement"
            element={
              <ProtectedRoute requiredModule="emargement">
                <Emargement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredModule="admin">
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin"
            element={
              <ProtectedRoute>
                <SuperAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute requireSubscription={false}>
                <Subscription />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute requireSubscription={false}>
                <Subscription />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/sepa"
            element={
              <ProtectedRoute requireSubscription={false}>
                <SEPAMandate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={<Navigate to="/documents" replace />}
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute requiredModule="documents">
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={<Navigate to="/absences" replace />}
          />
          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <EmployeePortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireSubscription={false}>
                <Settings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/formations"
            element={
              <ProtectedRoute requiredModule="formations">
                <Formations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sous-traitants"
            element={
              <ProtectedRoute requiredModule="sous_traitants">
                <SousTraitants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reporting"
            element={
              <ProtectedRoute requiredModule="reporting">
                <Reporting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute requiredModule="admin">
                <AuditLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prospects"
            element={
              <ProtectedRoute requireSubscription={false}>
                <Prospects />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
